/** 分镜脚本节点 · 风格库（workflowStyle）、画幅比例与输出语言 */

import {
  STORYBOARD_TIMELINE_FORMAT_EXAMPLE,
  buildStoryboardTimelineFormatHint
} from '@/utils/storyboardTimelineFormat'

export const STORYBOARD_LANGUAGE_OPTIONS = [
  { id: 'zh', label: '中文' },
  { id: 'en', label: 'English' }
]

export const STORYBOARD_DEFAULT_LANGUAGE = 'zh'

/** 与视频节点 data.ratio 一致 */
export const STORYBOARD_ASPECT_RATIO_OPTIONS = [
  { id: '16:9', label: '16:9 横屏' },
  { id: '9:16', label: '9:16 竖屏' }
]

export const STORYBOARD_DEFAULT_ASPECT_RATIO = '16:9'

/**
 * 拆镜节奏（解析剧本 → 镜头条数）
 * sparse：少镜长段；balanced：适中；dense：多镜短段，对白不易赶
 */
export const STORYBOARD_SHOT_PACE_OPTIONS = [
  { id: 'sparse', label: '疏 · 少镜' },
  { id: 'balanced', label: '适中' },
  { id: 'dense', label: '密 · 多镜' }
]
export const STORYBOARD_DEFAULT_SHOT_PACE = 'balanced'

/** 单镜建议时长（秒）；auto 由模型按节奏自定；最短约 4 秒，最长约 15 秒 */
export const STORYBOARD_TARGET_SHOT_SEC_OPTIONS = [
  { id: 'auto', label: '时长·自动' },
  { id: '4', label: '约 4 秒/镜' },
  { id: '5', label: '约 5 秒/镜' },
  { id: '8', label: '约 8 秒/镜' },
  { id: '15', label: '约 15 秒/镜' }
]
export const STORYBOARD_DEFAULT_TARGET_SHOT_SEC = 'auto'

/**
 * 对白拆分：one_beat=一句/一轮对白尽量独立成镜，避免两句挤一镜赶词
 */
export const STORYBOARD_DIALOGUE_SPLIT_OPTIONS = [
  { id: 'one_beat', label: '对白·一拍一镜' },
  { id: 'merge_ok', label: '对白·可合并' }
]
export const STORYBOARD_DEFAULT_DIALOGUE_SPLIT = 'one_beat'

/** 是否需要画面字幕（烧录/可读字幕，非仅口型对白） */
export const STORYBOARD_NEED_SUBTITLES_OPTIONS = [
  { id: 'no', label: '不要字幕' },
  { id: 'yes', label: '需要字幕' }
]
export const STORYBOARD_DEFAULT_NEED_SUBTITLES = 'no'

/** 是否需要背景音乐 */
export const STORYBOARD_NEED_BGM_OPTIONS = [
  { id: 'no', label: '不要BGM' },
  { id: 'yes', label: '需要BGM' }
]
export const STORYBOARD_DEFAULT_NEED_BGM = 'no'

/** 是否保留环境/动作音效（与 BGM 独立） */
export const STORYBOARD_NEED_SFX_OPTIONS = [
  { id: 'yes', label: '保留音效' },
  { id: 'no', label: '不要音效' }
]
export const STORYBOARD_DEFAULT_NEED_SFX = 'yes'

export function normalizeStoryboardYesNo(raw, defaultId) {
  const id = String(raw == null ? '' : raw)
    .trim()
    .toLowerCase()
  if (id === '1' || id === 'true' || id === 'yes' || id === 'y' || id === '需要' || id === '要') {
    return 'yes'
  }
  if (id === '0' || id === 'false' || id === 'no' || id === 'n' || id === '不要' || id === '否') {
    return 'no'
  }
  return defaultId
}

export function resolveStoryboardNeedSubtitles(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_NEED_SUBTITLES
  return normalizeStoryboardYesNo(d.storyboardNeedSubtitles, STORYBOARD_DEFAULT_NEED_SUBTITLES)
}

export function resolveStoryboardNeedBgm(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_NEED_BGM
  return normalizeStoryboardYesNo(d.storyboardNeedBgm, STORYBOARD_DEFAULT_NEED_BGM)
}

export function resolveStoryboardNeedSfx(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_NEED_SFX
  return normalizeStoryboardYesNo(d.storyboardNeedSfx, STORYBOARD_DEFAULT_NEED_SFX)
}

export function normalizeStoryboardShotPace(raw) {
  const id = String(raw || '')
    .trim()
    .toLowerCase()
  if (id === 'sparse' || id === 'coarse' || id === '少' || id === '疏') return 'sparse'
  if (id === 'dense' || id === 'fine' || id === '密' || id === '细') return 'dense'
  return STORYBOARD_DEFAULT_SHOT_PACE
}

export function normalizeStoryboardTargetShotSec(raw) {
  const id = String(raw == null ? '' : raw)
    .trim()
    .toLowerCase()
  if (id === '4' || id === '5' || id === '8' || id === '15') return id
  // 旧画布「约 3 秒」迁移为 4 秒
  if (id === '3') return '4'
  return STORYBOARD_DEFAULT_TARGET_SHOT_SEC
}

export function normalizeStoryboardDialogueSplit(raw) {
  const id = String(raw || '')
    .trim()
    .toLowerCase()
  if (id === 'merge_ok' || id === 'merge' || id === '可合并') return 'merge_ok'
  return STORYBOARD_DEFAULT_DIALOGUE_SPLIT
}

export function resolveStoryboardShotPace(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_SHOT_PACE
  return normalizeStoryboardShotPace(d.storyboardShotPace)
}

export function resolveStoryboardTargetShotSec(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_TARGET_SHOT_SEC
  return normalizeStoryboardTargetShotSec(d.storyboardTargetShotSec)
}

export function resolveStoryboardDialogueSplit(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_DIALOGUE_SPLIT
  return normalizeStoryboardDialogueSplit(d.storyboardDialogueSplit)
}

/** 分镜整理（↑）：DeepSeek 输出时间轴分镜稿 */
export const STORYBOARD_DEEPSEEK_SYSTEM_PROMPT = `你是专业分镜编剧与视频分镜策划。请结合「上游参考材料」与「用户分镜草稿」，输出一版可直接用于拍摄沟通与文生视频的时间轴分镜稿。

输出格式（必须严格遵守）：
- 全文为连续段落，每一段以「起始秒-结束秒：」开头（如 0-3秒：、3-4秒：），单段时长建议 1～15 秒；全片时间轴从 0 秒起连贯衔接，不重叠、不留空档（可合并或拆分镜头）。
- 每段在一行或数行内写清：景别/机位、运镜变化、主体动作与情绪；若有对白写「角色说：「…」」或「静静地说「…」」；段末用逗号串联参考说明，如「参考的分镜画面」「某某声音参考中的御姐音色」「背景参考」等（至少含「参考的分镜画面」）。
- 禁止输出表格、markdown 标题、镜号列表、JSON 或寒暄；直接给出分镜正文。
- 若用户消息含「分镜偏好」：须遵守其中的拆镜节奏、单镜时长、对白拆分，以及字幕/背景音乐/音效要求；宁可多分一段，也不要把两句对白挤进同一段导致赶词。videoPrompt 须与字幕与音频偏好一致（如不要字幕则勿写烧录字幕；不要 BGM 则明确禁止背景音乐）。

格式示例（内容须根据用户素材改写，勿照抄）：
${STORYBOARD_TIMELINE_FORMAT_EXAMPLE}`

/** 解析剧本 → 角色/道具/场景 + 镜头 JSON（供自动建图/视频节点） */
export const SCRIPT_TO_STORYBOARD_BUNDLE_JSON_PROMPT = `你是影视分镜、美术与视频生成策划。用户会提供「文学剧本」或「时间轴分镜稿」（段落以「0-3秒：」等形式起句，可能含上游参考资料）。
请输出**唯一一个 JSON 对象**（不要用 markdown 代码围栏，不要任何前言或后记），结构如下（键名必须一致）：
{
  "角色": [ { "名称": "简短中文名", "提示词": "用于 Seedream 文生图的中文提示（外貌服装体态，勿含镜号台词）" } ],
  "道具": [ { "名称": "…", "提示词": "…" } ],
  "场景": [ { "名称": "…", "提示词": "…" } ],
  "镜头": [
    {
      "镜号": "01",
      "durationSec": 5,
      "时间段": "0-3秒",
      "景别": "如：全景/中景/特写",
      "运镜": "如：固定/推/拉/摇",
      "画面描述": "构图、主体、光影氛围",
      "台词": "无 或 具体台词",
      "音效": "无 或 具体音效",
      "人物索引": [0],
      "道具索引": [0],
      "场景索引": [0],
      "videoPrompt": "与时间段一致的整段中文：以「0-3秒：」起句，含运镜、动作、台词与「参考的分镜画面」等说明（不要把 JSON 键名写进正文）"
    }
  ]
}
硬性规则：
1. "durationSec" 必须为 **1～15 的整数（秒）**，且与「时间段」起止秒差一致（不足 1 秒按 1 秒计）。**生成视频节点时**前端会将镜头合并为 **10～15 秒/节点**（短于 10 秒的连续段落会合并；超过 15 秒会拆条），模型仍可按时间轴原文逐段输出「镜头」。
2. 若输入为时间轴分镜稿：**每条「X-Y秒：」段落至少对应一条「镜头」**（勿把多条时间轴硬合成一条，除非单段已超过 15 秒需拆条）；「videoPrompt」须保留该段时间前缀与段内全文（可略润色，勿删对白与参考说明）。
3. "人物索引" / "道具索引" / "场景索引" 为**从 0 开始的整数数组**，分别对应上面 "角色" / "道具" / "场景" 数组的下标；本镜未出现则用 []。
4. 至少输出 1 条 "镜头"。若剧本中无明显某类资产，对应数组可为 []。
5. "提示词" 要具体可画；名称简短。若用户消息含「续集已定素材」且某资产名称与该块中已定案名称一致，则 JSON 中该条目的「提示词」**必须留空字符串 "" 或一字不改照抄块中摘要**，**禁止**根据剧本重新撰写或润色该提示词（前端会强制替换为画布节点上的真实提示词）。
6. "台词" 字段与 "videoPrompt" 必须一致：对白/旁白须写在 "台词" 中，且 **"videoPrompt" 内必须用自然语言复述或引用该对白**（若本镜无对白则 "台词" 填「无」、"videoPrompt" 中写明无对白即可），不得让对白只出现在 JSON 里而不进入 videoPrompt。
7. 若用户消息中含「续集已定素材」块：其中列出的角色/道具/场景在剧本中再次出现时，JSON 中**「名称」须与该块完全一致**（勿改字、勿换同义词）；新出现的角色/道具/场景照常新建「名称」与「提示词」。若与已定素材同名，前端将以**画布上对应图片节点内的提示词**覆盖 JSON 中的提示词，以保证与前一集一致。
8. 若用户消息含「分镜偏好」：必须遵守拆镜节奏、单镜建议时长、对白拆分，以及字幕/背景音乐/音效要求；**禁止为图省事合并关键对白节拍**。宁可多出 1～2 条镜头，也不要把本应分开的两句对白挤进同一镜导致赶词。videoPrompt 须与字幕与音频偏好一致。`

/** 旧版下拉风格 id → 风格库说明（仅迁移无 workflowStyle 的画布） */
const LEGACY_STYLE_HINTS = {
  cinematic: '写实电影感：自然光影、电影级构图与景深、克制调色。',
  anime: '动漫/二次元：清晰线稿或赛璐璐质感、鲜明配色、动感构图。',
  vertical_drama: '竖屏短剧：9:16 构图、人物特写、快切与强情绪对白。',
  documentary: '纪录片：纪实光影、自然光、真实环境细节。',
  commercial: '广告商业片：高完成度布光、节奏紧凑、画面干净有冲击力。',
  suspense: '悬疑氛围：低调光、高对比、压迫感构图。',
  warm_healing: '治愈清新：柔和自然光、暖色或低饱和、舒缓节奏。'
}

export function normalizeStoryboardLanguage(raw) {
  const id = String(raw || '')
    .trim()
    .toLowerCase()
  if (id === 'en' || id === 'english' || id === 'en-us' || id === 'en_us') return 'en'
  return 'zh'
}

export function resolveStoryboardWorkflowStyle(d) {
  if (!d || typeof d !== 'object') return { name: '', prompt: '' }
  const ws = d.workflowStyle && typeof d.workflowStyle === 'object' ? d.workflowStyle : null
  if (!ws) return { name: '', prompt: '' }
  return {
    name: String(ws.name || '').trim(),
    prompt: String(ws.prompt || '').trim()
  }
}

export function normalizeStoryboardAspectRatio(raw) {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
  if (s === '9:16' || s === '9x16' || s === '916' || s === 'vertical') return '9:16'
  return '16:9'
}

export function resolveStoryboardAspectRatio(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_ASPECT_RATIO
  const raw =
    d.storyboardAspectRatio != null
      ? d.storyboardAspectRatio
      : d.aspectRatio != null
        ? d.aspectRatio
        : ''
  return normalizeStoryboardAspectRatio(raw || STORYBOARD_DEFAULT_ASPECT_RATIO)
}

/** 图片节点 aspectKey 的 ratioSlug 部分 */
export function storyboardAspectRatioToImageRatioSlug(aspectRatio) {
  return normalizeStoryboardAspectRatio(aspectRatio) === '9:16' ? '9x16' : '16x9'
}

/** @param {string} storyboardRatio '16:9' | '9:16' */
export function buildImageAspectKeyFromStoryboardRatio(storyboardRatio, tier = '2k') {
  const slug = storyboardAspectRatioToImageRatioSlug(storyboardRatio)
  const t = String(tier || '2k').trim().toLowerCase()
  const tierNorm = t === '1k' || t === '4k' ? t : '2k'
  return `${slug}_${tierNorm}`
}

export function storyboardAspectRatioFromNode(sbNode) {
  if (
    !sbNode ||
    !sbNode.data ||
    (sbNode.type !== 'storyboard' && sbNode.type !== 'storyPanel')
  ) {
    return STORYBOARD_DEFAULT_ASPECT_RATIO
  }
  return resolveStoryboardAspectRatio(sbNode.data)
}

export function resolveStoryboardLanguage(d) {
  const raw =
    d && d.storyboardLanguage != null
      ? d.storyboardLanguage
      : d && d.outputLanguage != null
        ? d.outputLanguage
        : ''
  return normalizeStoryboardLanguage(raw || STORYBOARD_DEFAULT_LANGUAGE)
}

export function isStoryboardEnglishOutput(langOrData) {
  if (langOrData && typeof langOrData === 'object') {
    return resolveStoryboardLanguage(langOrData) === 'en'
  }
  return normalizeStoryboardLanguage(langOrData) === 'en'
}

/** 整理分镜表 / 解析 JSON 时写入 user 的偏好块 */
export function buildStoryboardPreferencesUserBlock(d) {
  if (!d || typeof d !== 'object') return ''
  const parts = []
  const { name, prompt } = resolveStoryboardWorkflowStyle(d)
  if (name || prompt) {
    parts.push(name ? `视觉风格（风格库）：${name}` : '视觉风格（风格库）')
    if (prompt) {
      parts.push(
        `风格库提示词（须贯彻到分镜画面描述、角色/道具/场景文生图提示词与 videoPrompt 的构图、光影、色彩与质感）：\n${prompt}`
      )
    }
  } else {
    const legacyHint = legacyStoryboardStyleHintFromOldFields(d)
    if (legacyHint) parts.push(`视觉风格说明：${legacyHint}`)
  }
  const ar = resolveStoryboardAspectRatio(d)
  parts.push(
    ar === '9:16'
      ? '画幅比例：9:16 竖屏。分镜构图、角色/道具/场景文生图与视频生成均按竖屏画幅。'
      : '画幅比例：16:9 横屏。分镜构图、角色/道具/场景文生图与视频生成均按横屏画幅。'
  )
  const en = isStoryboardEnglishOutput(d)
  parts.push(
    en
      ? '输出语言：English。时间轴分镜段落、镜头「台词」、旁白、音效说明，以及 videoPrompt / 文生图「提示词」中的可读文案均使用英文（专有名词可保留原文）。'
      : '输出语言：简体中文。时间轴分镜段落、镜头「台词」、旁白、音效说明，以及 videoPrompt / 文生图「提示词」中的可读文案均使用简体中文。'
  )

  const pace = resolveStoryboardShotPace(d)
  if (pace === 'sparse') {
    parts.push(
      '拆镜节奏：疏（少镜）。在不丢失关键情节与对白的前提下，可适当合并相邻动作；但仍须保证每句重要对白有足够口型与气口时间，禁止把两句对白硬塞进同一镜导致赶词。'
    )
  } else if (pace === 'dense') {
    parts.push(
      '拆镜节奏：密（多镜）。优先细拆：景别变化、动作转折、情绪转折、每句/每轮对白尽量独立成镜；宁可多 1～2 条镜头，也不要合并过度。文学剧本里明显可拍成 3 个节拍的内容，禁止压成 2 条。'
    )
  } else {
    parts.push(
      '拆镜节奏：适中。按叙事节拍拆镜；有独立对白、反应镜头或景别跳变时须分开成镜，避免对白赶词。'
    )
  }

  const targetSec = resolveStoryboardTargetShotSec(d)
  if (targetSec === '4') {
    parts.push('单镜建议时长：约 4 秒/镜（多数镜头 3～5 秒；对白镜可略长到 6 秒，仍须拆开不同说话人）。')
  } else if (targetSec === '5') {
    parts.push('单镜建议时长：约 5 秒/镜（多数镜头 4～6 秒；复杂动作可到 8 秒，勿把无关对白并进同一镜）。')
  } else if (targetSec === '8') {
    parts.push('单镜建议时长：约 8 秒/镜（多数镜头 6～10 秒；仍须按对白与景别合理拆分）。')
  } else if (targetSec === '15') {
    parts.push('单镜建议时长：约 15 秒/镜（多数镜头 12～15 秒；长段可含连续动作，但对白换人仍建议拆镜，勿赶词）。')
  } else {
    parts.push('单镜建议时长：自动（在 1～15 秒内按动作与对白需要分配；对白镜优先给足气口，勿压缩到赶词）。')
  }

  const dialogueSplit = resolveStoryboardDialogueSplit(d)
  if (dialogueSplit === 'merge_ok') {
    parts.push(
      '对白拆分：可合并。同一场景内极短接话可同镜，但超过约 12 字或换说话人时仍建议拆镜。'
    )
  } else {
    parts.push(
      '对白拆分：一拍一镜。不同角色各说一句、或同一角色两句之间有反应/停顿时，必须拆成独立镜头；禁止把 A 说完紧接 B 说完挤在同一 durationSec 里导致赶词。'
    )
  }

  const needSubs = resolveStoryboardNeedSubtitles(d)
  if (needSubs === 'yes') {
    parts.push(
      '字幕：需要。videoPrompt / 时间轴正文须写明「画面需可读字幕（对白/关键旁白）」，字幕简洁、不遮挡主体；勿输出大段滚动字幕。'
    )
  } else {
    parts.push(
      '字幕：不需要。videoPrompt 写明「不要画面字幕/烧录字幕」，对白仅靠口型与声音表现。'
    )
  }

  const needBgm = resolveStoryboardNeedBgm(d)
  const needSfx = resolveStoryboardNeedSfx(d)
  if (needBgm === 'yes' && needSfx === 'yes') {
    parts.push(
      '音频：需要背景音乐，并保留环境声/动作音效与对白气口；videoPrompt 中注明 BGM 情绪倾向（可简短）。'
    )
  } else if (needBgm === 'yes' && needSfx === 'no') {
    parts.push(
      '音频：需要背景音乐；尽量弱化或不要额外音效层；仍可保留必要对白气口。'
    )
  } else if (needBgm === 'no' && needSfx === 'no') {
    parts.push(
      '音频：不要背景音乐，也不要额外音效；仅保留必要对白气口（或静音对白镜说明）。'
    )
  } else {
    parts.push(
      '音频：不要背景音乐，只保留正常的音效（环境声、动作声、必要对白气口）。'
    )
  }

  parts.push(buildStoryboardTimelineFormatHint(en))
  return parts.filter(Boolean).join('\n')
}

function legacyStoryboardStyleHintFromOldFields(d) {
  if (!d || typeof d !== 'object') return ''
  const pick = d.storyboardStylePick != null ? String(d.storyboardStylePick).trim() : ''
  if (pick === '__custom__') {
    const custom = String(d.storyboardStyleCustom || '').trim()
    return custom ? `用户自定义视觉风格：${custom}` : ''
  }
  if (pick && LEGACY_STYLE_HINTS[pick]) return LEGACY_STYLE_HINTS[pick]
  return String(d.storyboardStyle || '').trim()
}

/**
 * 故事板关键帧出图：整页多格分镜表（对标「故事线与面板集」样式）
 * 单张图内含多格黑白铅笔分镜 + 彩色运镜/运动标注 + 中文面板标题
 * @param {object} sh 镜头（可为合并后的 10～15 秒镜组）
 * @param {object} opts
 * @param {string} [opts.aspectRatio]
 * @param {number} [opts.panelIndex] 0-based（本段在全片中的序号）
 * @param {number} [opts.panelTotal]
 * @param {number} [opts.panelCount] 本页格子数，默认按时长估算，上限 12
 * @param {{ kindZh: string, name: string, imgTag?: string }[]} [opts.metas]
 * @param {string} [opts.styleName]
 * @param {string} [opts.stylePrompt]
 * @param {string} [opts.scriptHook]
 */
export function buildCinematicStoryPanelImagePrompt(sh, opts = {}) {
  const shotNo = String(sh && sh.shotNo != null ? sh.shotNo : '').trim() || '—'
  const composition = String(sh && sh.composition != null ? sh.composition : '')
    .trim()
    .replace(/^—$/, '')
  const jingbie = String(sh && sh.jingbie != null ? sh.jingbie : '')
    .trim()
    .replace(/^—$/, '')
  const yunjing = String(sh && sh.yunjing != null ? sh.yunjing : '')
    .trim()
    .replace(/^—$/, '')
  const dialogue = String(sh && sh.dialogue != null ? sh.dialogue : '')
    .trim()
    .replace(/^无$/, '')
  const sfx = String(sh && sh.sfx != null ? sh.sfx : '')
    .trim()
    .replace(/^无$/, '')
  let vp = String(sh && sh.videoPrompt != null ? sh.videoPrompt : '').trim()
  // 去掉时间戳前缀，故事板图禁止时间戳
  vp = vp.replace(/^\d+(?:\.\d+)?\s*[-~–—至到]\s*\d+(?:\.\d+)?\s*秒?\s*[：:]\s*/i, '').trim()

  const duration = Math.min(
    15,
    Math.max(1, Math.round(Number(opts.durationSec != null ? opts.durationSec : sh && sh.durationSec) || 5))
  )
  const ar = normalizeStoryboardAspectRatio(opts.aspectRatio || '16:9')
  const arLabel = ar === '9:16' ? '9:16 竖屏' : '16:9 横屏'
  const idx = Number(opts.panelIndex)
  const total = Number(opts.panelTotal)
  const seqLabel =
    Number.isFinite(idx) && Number.isFinite(total) && total > 0
      ? `全片第 ${idx + 1}/${total} 段（镜号 ${shotNo}）`
      : `镜号 ${shotNo}`

  // 对标参考图：约 12 格电影分镜表；按时长略调，最少 6、最多 12
  let panelCount = Math.round(Number(opts.panelCount) || 0)
  if (!Number.isFinite(panelCount) || panelCount < 1) {
    panelCount = Math.min(12, Math.max(6, Math.round(duration * 0.9)))
  }
  panelCount = Math.min(12, Math.max(6, panelCount))
  const gridHint =
    panelCount <= 6
      ? '2 行 × 3 列'
      : panelCount <= 8
        ? '2 行 × 4 列'
        : panelCount <= 9
          ? '3 行 × 3 列'
          : '3 行 × 4 列'

  const metas = Array.isArray(opts.metas) ? opts.metas : []
  const refLines = []
  const mapBits = []
  let imgSeq = 0
  for (const m of metas) {
    if (!m) continue
    const kind = String(m.kindZh || '').trim()
    const name = String(m.name || '').trim() || '未命名'
    imgSeq += 1
    const imgTag = String(m.imgTag || '').trim() || `[图片${imgSeq}]`
    const imgNumMatch = /(\d+)/.exec(imgTag)
    const imgNum = imgNumMatch ? parseInt(imgNumMatch[1], 10) : imgSeq
    if (kind === '人物') {
      const portraitTag = `{{Portrait ${imgNum}}}`
      refLines.push(
        `使用参考图像 ${portraitTag} 作为角色「${name}」（全页所有格子保持同一身份与服装，勿改脸）。`
      )
      mapBits.push(`${portraitTag}（人物·${name}）`)
    } else if (kind === '道具') {
      refLines.push(`使用参考图像 ${imgTag} 作为道具「${name}」，须在相关格子中可辨认。`)
      mapBits.push(`${imgTag}（道具·${name}）`)
    } else if (kind === '场景') {
      refLines.push(`使用参考图像 ${imgTag} 作为场景「${name}」的空间结构与氛围参考（全页环境连贯）。`)
      mapBits.push(`${imgTag}（场景·${name}）`)
    } else {
      refLines.push(`使用参考图像 ${imgTag} 作为「${name}」参考。`)
      mapBits.push(`${imgTag}（${name}）`)
    }
  }

  const hook = String(opts.scriptHook || '').trim()
  const actionBody =
    composition ||
    vp ||
    [jingbie && `景别 ${jingbie}`, yunjing && `运镜 ${yunjing}`].filter(Boolean).join('；') ||
    '根据剧本推进的高张力关键动作流'

  const styleName = String(opts.styleName || '').trim()
  const stylePrompt = String(opts.stylePrompt || '').trim()

  const parts = []
  parts.push(
    hook
      ? `为故事板创建一张完整的「故事线与面板集」分镜表，主题与剧本一致：${hook.slice(0, 180)}。专注于强烈视觉对比、运动能量、电影构图，以及跨面板的角色/场景一致性。`
      : `为故事板创建一张完整的「故事线与面板集」电影分镜表，专注于运动能量、构图张力、角色情绪释放，以及跨面板主体一致。`
  )
  if (refLines.length) parts.push(refLines.join('\n'))
  if (mapBits.length) {
    parts.push(`【参考图对应】${mapBits.join('；')}（须与上方素材缩略图序号一致）`)
  }
  parts.push(
    `${arLabel} 故事板表格，${seqLabel}，共 ${panelCount} 个电影风格面板（建议布局 ${gridHint}，整齐网格，白边分隔）。` +
      `实际故事板绘图必须仅为黑白主体：粗糙的铅笔线条、最小细节、快速手势绘图能量、简单的解剖结构构建以及强烈的轮廓可读性。保持艺术作品轻量、动态且未完成，就像早期的编舞/分镜预览。彩色仅用于标注箭头与笔记，禁止彩色填色主画面。`
  )
  parts.push(
    `按时间顺序把本段约 ${duration} 秒剧情拆成 ${panelCount} 个连续节拍，每格一个关键动作瞬间。叙事来源：\n${actionBody}`
  )
  if (jingbie || yunjing) {
    parts.push(
      [jingbie && `本段景别倾向：${jingbie}`, yunjing && `运镜倾向：${yunjing}`]
        .filter(Boolean)
        .join('。') + '（各格景别应有变化：建立全景、跟拍、特写、主观视角、英雄低机位等交错。）'
    )
  } else {
    parts.push(
      '各格景别须有变化：建立全景、低机位加速、面部特写、侧面跟拍、极端特写、主观 POV、环绕动态、终帧剪影等交错出现，避免十二格雷同构图。'
    )
  }
  if (dialogue) {
    parts.push(`对白/情绪（可用紫色短记点出，勿在画面写大段可读字幕）：${dialogue}`)
  }
  if (sfx) {
    parts.push(`声音/环境音（紫色标记）：${sfx}`)
  }
  parts.push(
    '每个面板必须包含可见的运动和强烈的身体/物体动量（速度线、扬尘、衣摆、车辆前倾等）。避免静态站立姿势。角色与关键道具在全页保持一致。'
  )
  parts.push(
    '每个面板底部用黑色短标题（中文）标注景别与动作，例如：「全景建立 / 公路远景」「低机位加速 / 后轮扬尘」「侧跟 / 速度拉伸」「终帧冲刺 / 逆光剪影」。标题简洁，勿遮挡主体。'
  )
  parts.push(
    '使用电影艺术风格的摄影技巧：手持能量、快速平移、环绕运动、头顶镜头、侧面轮廓、侵略性特写、长焦压缩、极端负空间、微距机械细节、主观 POV。'
  )
  parts.push('保持环境最小化：只保留对叙事必要的空间轮廓、关键光影与材质暗示，避免过度细节堆砌。')
  parts.push(
    [
      '标注颜色系统（必须画在格子内）：',
      '红色箭头 = 身体 / 载具运动方向',
      '蓝色箭头 = 摄影机运动（推/拉/摇/环绕等）',
      '绿色标记 = 取景 / 构图 / 镜头笔记',
      '橙色标记 = 灯光方向（逆光、侧光、镜头眩光）',
      '紫色标记 = 声音 / 情感强调',
      '黑色文本 = 面板序号与底部短标题'
    ].join('\n')
  )
  parts.push(
    '页脚保留简洁图例（红/蓝/绿/橙/紫含义），右下角可写「故事线与面板集」小字。无时间戳、无水印、无照片级写实。最后一格以压倒性的高潮姿势或仪式感定格收束全页。'
  )
  if (styleName || stylePrompt) {
    parts.push(
      [
        '视觉风格补充（须融入铅笔分镜的光影与构图气质，但仍保持黑白线稿主体）：',
        styleName ? `风格名：${styleName}` : '',
        stylePrompt ? stylePrompt : ''
      ]
        .filter(Boolean)
        .join('\n')
    )
  }
  parts.push('禁止：彩色填色主画面、照片级写实、大段可读字幕、水印、时间码、单格铺满整张（必须是多格表）。')
  return parts.filter(Boolean).join('\n\n')
}

/**
 * 故事板衍生视频提示词（电影叙事 / Mixed 参考风格）
 * @param {object} sh 镜头
 * @param {object} opts
 * @param {number} [opts.durationSec]
 * @param {number} [opts.beatCount] 本镜内节拍数暗示
 * @param {number} [opts.panelIndex]
 * @param {number} [opts.panelTotal]
 * @param {string} [opts.scriptHook]
 * @param {string} [opts.styleName]
 * @param {string} [opts.stylePrompt]
 * @param {boolean} [opts.needSubtitles]
 * @param {boolean} [opts.needBgm]
 * @param {boolean} [opts.needSfx]
 * @param {{ mixed: number, imgTag: string, kindZh: string, name: string }[]} [opts.refs]
 *   refs 建议顺序：故事板 → 人物 → 场景/风格
 */
export function buildCinematicStoryPanelVideoPrompt(sh, opts = {}) {
  const duration = Math.min(
    15,
    Math.max(1, Math.round(Number(opts.durationSec != null ? opts.durationSec : sh && sh.durationSec) || 5))
  )
  const shotNo = String(sh && sh.shotNo != null ? sh.shotNo : '').trim() || '—'
  const composition = String(sh && sh.composition != null ? sh.composition : '')
    .trim()
    .replace(/^—$/, '')
  const jingbie = String(sh && sh.jingbie != null ? sh.jingbie : '')
    .trim()
    .replace(/^—$/, '')
  const yunjing = String(sh && sh.yunjing != null ? sh.yunjing : '')
    .trim()
    .replace(/^—$/, '')
  const dialogue = String(sh && sh.dialogue != null ? sh.dialogue : '')
    .trim()
    .replace(/^无$/, '')
  const sfx = String(sh && sh.sfx != null ? sh.sfx : '')
    .trim()
    .replace(/^无$/, '')
  let vp = String(sh && sh.videoPrompt != null ? sh.videoPrompt : '').trim()
  vp = vp.replace(/^\d+(?:\.\d+)?\s*[-~–—至到]\s*\d+(?:\.\d+)?\s*秒?\s*[：:]\s*/i, '').trim()

  const needSubtitles = opts.needSubtitles === true
  const needBgm = opts.needBgm === true
  const needSfx = opts.needSfx !== false

  const actionBody =
    composition ||
    vp ||
    [jingbie && `景别 ${jingbie}`, yunjing && `运镜 ${yunjing}`].filter(Boolean).join('；') ||
    '按剧本推进的高张力动作流'

  const beatHint = Math.max(
    1,
    Math.round(Number(opts.beatCount) || Math.min(12, Math.max(3, Math.round(duration * 0.8))))
  )
  const idx = Number(opts.panelIndex)
  const total = Number(opts.panelTotal)
  const seqLabel =
    Number.isFinite(idx) && Number.isFinite(total) && total > 0
      ? `全片第 ${idx + 1}/${total} 段（镜号 ${shotNo}）`
      : `镜号 ${shotNo}`

  const refs = Array.isArray(opts.refs) ? opts.refs.filter(Boolean) : []
  const byMixed = new Map()
  for (const r of refs) {
    const m = Number(r.mixed)
    if (Number.isFinite(m) && m > 0 && !byMixed.has(m)) byMixed.set(m, r)
  }
  const mixed1 = byMixed.get(1)
  const mixed2 = byMixed.get(2)
  const mixed3 = byMixed.get(3)

  const tagOf = r => {
    if (!r) return ''
    const mixed = Number(r.mixed)
    if (Number.isFinite(mixed) && mixed > 0) return `{{Mixed ${mixed}}}`
    return String(r.imgTag || '').trim()
  }

  const hook = String(opts.scriptHook || '').trim()
  const styleName = String(opts.styleName || '').trim()
  const stylePrompt = String(opts.stylePrompt || '').trim()

  const audioLine = (() => {
    if (needBgm && needSfx) {
      return '需要合适的背景音乐，并保留正常的环境声、动作音效与对白气口。'
    }
    if (needBgm && !needSfx) {
      return '需要背景音乐；尽量不要额外音效层，可保留必要对白气口。'
    }
    if (!needBgm && !needSfx) {
      return '不要背景音乐，也不要额外音效；仅保留必要对白气口。'
    }
    return '不要背景音乐，只保留正常的音效（环境声、动作声、必要对白气口）。'
  })()

  const parts = []
  const storyRef = tagOf(mixed1) || '{{Mixed 1}}'
  parts.push(
    `使用故事板参考 ${storyRef} 作为 ${duration} 秒视频的叙事来源。依次遵循约 ${beatHint} 个节拍（${seqLabel}）。` +
      (hook ? `保留剧本核心情绪与反差递进：${hook.slice(0, 140)}。` : '') +
      `保持分镜顺序、构图变化、主体体积/动作对比，以及最终的情感高潮定格。`
  )

  const styleRef = tagOf(mixed3)
  if (styleRef) {
    parts.push(`影片风格和色调严格复刻 ${styleRef} 。${audioLine}`)
  } else if (styleName || stylePrompt) {
    parts.push(
      [
        '影片风格和色调须统一：',
        styleName ? `风格「${styleName}」` : '',
        stylePrompt || '',
        audioLine
      ]
        .filter(Boolean)
        .join('')
    )
  } else {
    parts.push(audioLine)
  }

  parts.push(
    `将本镜节拍序列压缩到 ${duration} 秒内。每个节拍需展现清晰的动作快照与情绪推进。使用硬切与动作匹配剪辑（动作轨迹、视线、物体位移可无缝衔接）。保持强烈视觉碰撞与节奏张力。`
  )

  const charRef = tagOf(mixed2)
  if (charRef) {
    const cname = String((mixed2 && mixed2.name) || '主角').trim()
    parts.push(
      `保持绝对核心主体身份：「${cname}」 ${charRef} 。保留其外貌与服装。展现连贯动作流：${actionBody}`
    )
  } else {
    parts.push(`保持角色身份连贯，展现连贯动作流：${actionBody}`)
  }

  if (jingbie || yunjing) {
    parts.push(
      [
        '电影镜头风格：',
        jingbie ? `景别 ${jingbie}` : '',
        yunjing ? `运镜 ${yunjing}` : '',
        '可用微距浅景深、过肩、低角度、侧面轮廓与侵略性特写强化反差。'
      ]
        .filter(Boolean)
        .join(' ')
    )
  } else {
    parts.push(
      '电影镜头风格：极端微距浅景深、过肩专注氛围、低角度特写、侧面轮廓与硬核写实光影，突显高级反差。'
    )
  }

  if (dialogue) {
    parts.push(
      needSubtitles
        ? `对白/气口：${dialogue}。画面需同步简洁可读字幕（勿大段滚动）。`
        : `对白/气口（可极短，不要画面字幕）：${dialogue}`
    )
  } else if (needSubtitles) {
    parts.push('如有旁白或关键信息，可加简洁可读字幕；勿大段滚动字幕。')
  } else {
    parts.push('不要画面字幕/烧录字幕。')
  }
  if (sfx && needSfx) parts.push(`音效重点：${sfx}`)

  parts.push(
    '最终节拍（情感升华）：在动作高潮后转入更完整、更有仪式感的构图；光线可略转暖或更戏剧化，静谧定格约 1 秒，留下视觉反差与情绪余音。'
  )

  if (refs.length) {
    const mapLine = refs
      .map(r => {
        const m = Number(r.mixed)
        const kind = String(r.kindZh || '').trim()
        const name = String(r.name || '').trim()
        const label = kind ? `${kind}${name ? '·' + name : ''}` : name
        return `{{Mixed ${m}}}${label ? `（${label}）` : ''}`
      })
      .join('；')
    parts.push(`【参考图对应】${mapLine}`)
  }

  return parts.filter(Boolean).join('\n\n')
}
